<template>
  <div>
    <v-data-table
      :headers="headers"
      :items="campaignTags"
      :items-per-page="-1"
      :loading="loading"
      height="70vh"
      class="elevation-3"
      hide-default-footer>
      <template v-slot:top>
        <v-toolbar flat>
          <v-row
            justify="space-between"
            no-gutters>
            <v-col cols="3">
              <v-toolbar-title>
                <span class="capitalize">
                  Campaign Tags
                </span>
              </v-toolbar-title>
            </v-col>
            <v-col cols="9">
              <v-row
                justify="end"
                no-gutters>
                <v-col cols="3">
                  <v-select
                    v-model="selectedBrand"
                    :items="brands"
                    label="Brand"
                    dense
                    outlined
                    hide-details
                    class="mr-1" />
                </v-col>
                <v-col cols="4">
                  <search-box
                    v-model="inputSearch"
                    class="ml-1"
                    @on-search="getCampaignTags()" />
                </v-col>
                <v-col cols="auto">
                  <v-btn
                    class="ml-2"
                    color="primary"
                    @click="dialogToggle()">
                    Create
                  </v-btn>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-dialog
            v-if="dialog"
            v-model="dialog"
            max-width="500px">
            <v-card>
              <campaign-tag-form-data
                v-model="formData"
                :title="formTitle"
                :tags="tags"
                :brands="brands"
                @on-save="saveForm()"
                @on-close="closeFormModal()" />
            </v-card>
          </v-dialog>
        </v-toolbar>
      </template>
      <template v-slot:[`item.tags`]="{ item }">
        <v-chip
          v-for="(tag, tIndex) in item.tags"
          :key="`tag-chip-${tIndex}`"
          class="ma-1"
          color="secondary"
          text-color="white"
          small
          label>
          {{ tag.name }}
        </v-chip>
      </template>
      <template v-slot:[`item.date`]="{ item }">
        {{ getDateFormat(item.date) }}
      </template>
      <template v-slot:[`item.note`]="{ item }">
        {{ item.note }}
      </template>
      <template v-slot:[`item.actions`]="{ item }">
        <v-icon
          class="mr-2"
          small
          @click="editItem(item)">
          mdi-pencil
        </v-icon>
        <gw-icon-hover
          class="sku-table-bin"
          icon-name="mdi-delete"
          icon-hover="mdi-delete-empty"
          small
          @click="removeCampaignTag(item)" />
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import SearchBox from '@/components/SearchBox.vue'
import WebManagementProvider from '@/resources/WebManagementProvider'
import ProductAttributeProvider from '@/resources/ProductAttributeProvider'
import CampaignTagFormData from './CampaignTagFormData.vue'

const ProductAttributeService = new ProductAttributeProvider()
const WebManagementService = new WebManagementProvider()

export default {
  components: { SearchBox, CampaignTagFormData },
  data () {
    return {
      headers: [
        {
          text: 'Tags',
          value: 'tags',
          align: 'center',
          sortable: true
        },
        {
          text: 'Start Date',
          value: 'date',
          align: 'center',
          sortable: false
        },
        {
          text: 'Brand',
          value: 'brand',
          align: 'center',
          sortable: false
        },
        {
          text: 'Note',
          value: 'note',
          align: 'center',
          sortable: false
        },
        {
          text: 'Actions',
          value: 'actions',
          sortable: false,
          width: '100px',
          align: 'start'
        }
      ],
      inputSearch: '',
      selectedBrand: null,
      brands: [],
      dialog: false,
      isEdited: false,
      loading: false,
      formData: {
        id: '',
        tags: [],
        date: '',
        note: '',
        brand: null,
        status: 'active'
      },
      tags: [],
      campaignTags: []
    }
  },
  computed: {
    formTitle () {
      return this.isEdited ? 'Edit Campaign Tag' : 'New Campaign Tag'
    }
  },
  watch: {
    selectedBrand (val) {
      this.formData.brand = val
      this.getCampaignTags()
    }
  },
  async mounted () {
    await Promise.all([
      this.getTags(),
      this.getCampaignTags(),
      this.getBrand()
    ])
  },
  methods: {
    ...mapActions({
      setSnackbar: 'Components/setSnackbar',
      setErrorPage: 'Components/setErrorPage',
      setModal: 'Components/setModal'
    }),
    async getBrand () {
      try {
        const { data } = await ProductAttributeService.getProductAttribute('brands', {
          limit: 999
        })

        this.brands = data.results.map((brand) => brand.name)
      } catch (error) {
        console.error('getAttribute: ', error)
      }
    },
    getDateFormat (date) {
      return this.$dayjs(date).format('DD/MM/YYYY HH:mm')
    },
    dialogToggle () {
      this.clearForm()
      this.dialog = true
    },
    closeFormModal () {
      this.dialog = false
      this.isEdited = false
      this.clearForm()
    },
    editItem (item) {
      this.isEdited = true
      this.formData = { ...item }
      this.dialog = true
    },
    clearForm () {
      this.formData = {
        id: '',
        tags: [],
        date: this.$dayjs().utc().toISOString(),
        note: '',
        brand: this.selectedBrand,
        status: 'active'
      }
    },
    async getTags () {
      try {
        this.loading = true

        const { data } = await ProductAttributeService.getProductAttribute('tags', {
          page: 1,
          itemsPerPage: 9999,
          sortBy: ['id'],
          sortDesc: [true]
        })

        this.tags = data.results.map((r) => ({
          id: r.id,
          name: r.name,
          status: 'active'
        }))
      } catch (error) {
        console.error('getTags: ', error)
        this.setSnackbar({
          value: true,
          message: `Error ${error.code} : ${error.message}`,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    async getCampaignTags () {
      try {
        this.loading = true

        const { data } = await WebManagementService.getCampaignTags({
          search: this.inputSearch,
          brand: this.selectedBrand
        })
        this.campaignTags = data
      } catch (error) {
        console.error('getCampaignTags', error)
        this.setSnackbar({
          value: true,
          message: `Error ${error.code} : ${error.message}`,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    removeCampaignTag (item) {
      this.setModal({
        value: true,
        title: 'Remove Campaign Tag',
        message: 'Do you want to remove the Campaign Tag?',
        confirmText: 'Remove',
        confirmType: 'error',
        cancelType: '',
        cancelText: 'Cancel',
        onConfirm: () => this.confirmRemoveCampaignTag(item.id)
      })
    },
    async confirmRemoveCampaignTag (id) {
      try {
        this.loading = true

        const { data } = await WebManagementService.deleteCampaignTag(id)

        if (data) {
          this.setSnackbar({
            value: true,
            message: 'Campaign Tag removed.',
            type: 'success'
          })

          this.getCampaignTags()
        } else {
          const error = {
            code: 400,
            message: 'Campaign Tag ID not found or status not change'
          }
          throw error
        }
      } catch (error) {
        console.error('confirmRemoveCampaignTag', error)
        this.setSnackbar({
          value: true,
          message: `Error ${error.code} : ${error.message}`,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    async saveForm () {
      let isSuccess = false
      let message = 'created'
      try {
        if (this.isEdited) {
          const { data } = await WebManagementService.updateCampaignTag(this.formData)

          if (data) {
            isSuccess = true
          }
          message = 'updated'
        } else {
          const { data } = await WebManagementService.createCampaignTag(this.formData)

          if (data) {
            isSuccess = true
          }
        }
      } catch (error) {
        this.setSnackbar({
          value: true,
          message: `Error code ${error.code}: ${error.message}`,
          type: 'error'
        })
        console.error('saveForm', error)
      } finally {
        if (isSuccess) {
          this.setSnackbar({
            value: true,
            message: `Campaign Tag ${message}`,
            type: 'success'
          })
          this.closeFormModal()
          this.getCampaignTags()
        } else {
          this.setSnackbar({
            value: true,
            message: `Campaign Tag ${message} failed.`,
            type: 'error'
          })
        }
      }
    }
  }
}
</script>

<style scoped>
.mini-image {
  cursor: pointer;
}
</style>
